import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="company-cegadat-api"
export default class extends Controller {
  static targets = [
    "nameInput",
    "taxNumberInput",
    "resultsContainer",
    "loadingIndicator"
  ]
  static values = {
    searchUrl: String,
    detailUrl: String
  }

  connect() {
    this.searchTimeout = null
    this.currentSearchValue = ""
    this.activeInput = null
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // Handle name input changes
  onNameInput(event) {
    this.activeInput = event.target
    const value = event.target.value.trim()
    if (value.length < 4) {
      this.hideResults()
      this.hideLoading()
      return
    }

    this.debounceSearch(() => this.searchByName(value))
  }

  // Handle tax number input changes
  onTaxNumberInput(event) {
    this.activeInput = event.target
    // Remove all non-digit characters (hyphens, spaces, etc.) for search
    const digitsOnly = event.target.value.trim().replace(/\D/g, '')
    
    // Allow search with at least 8 digits (partial tax number) or full 11 digits
    if (digitsOnly.length < 8) {
      this.hideResults()
      this.hideLoading()
      return
    }

    // Always send digits only (without hyphens) to the API
    this.debounceSearch(() => this.searchByTaxNumber(digitsOnly))
  }

  debounceSearch(searchFunction) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.showLoading()

    this.searchTimeout = setTimeout(() => {
      searchFunction()
      this.searchTimeout = null
    }, 1000) // 1 second debounce
  }

  async searchByName(name) {
    try {
      const response = await get(this.searchUrlValue, {
        query: { name: name, business_type: 'all' },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.displayResults(data)
      } else {
        this.hideResults()
        this.hideLoading()
      }
    } catch (error) {
      console.error("Search error:", error)
      this.hideResults()
      this.hideLoading()
    }
  }

  async searchByTaxNumber(taxNumber) {
    try {
      const response = await get(this.searchUrlValue, {
        query: { vat_number: taxNumber, business_type: 'all' },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.displayResults(data)
      } else {
        this.hideResults()
        this.hideLoading()
      }
    } catch (error) {
      console.error("Search error:", error)
      this.hideResults()
      this.hideLoading()
    }
  }

  displayResults(data) {
    this.hideLoading()

    if (!data.status || !data.response || !data.response.results || data.response.results.length === 0) {
      this.hideResults()
      return
    }

    if (!this.activeInput) {
      return
    }

    const results = data.response.results
    
    // Find or create container for the active input
    let container = this.activeInput.parentElement.querySelector('[data-company-cegadat-api-target="resultsContainer"]')
    if (!container) {
      container = document.createElement("div")
      container.setAttribute("data-company-cegadat-api-target", "resultsContainer")
      container.className = "d-none"
      this.activeInput.parentElement.appendChild(container)
    }

    // Clear previous results
    container.innerHTML = ""

    // Add Bootstrap classes to container
    container.className = "dropdown-menu show"
    container.classList.remove("d-none")
    
    // Create results list - use dropdown-item instead of list-group to avoid double border
    results.forEach((company) => {
      const item = document.createElement("a")
      item.className = "dropdown-item"
      item.href = "#"
      item.innerHTML = `
        <div class="fw-bold mb-1">${this.escapeHtml(company.fullName || company.shortName || '')}</div>
        ${company.vatNumber ? `<div class="text-muted small">Adószám: ${this.formatTaxNumber(company.vatNumber)}</div>` : ''}
        ${company.status ? `<div class="text-muted small">Státusz: ${company.status}</div>` : ''}
      `

      item.addEventListener("click", (e) => {
        e.preventDefault()
        this.selectCompany(company)
      })

      container.appendChild(item)
    })
  }

  async selectCompany(company) {
    if (!company.vatNumber) {
      console.error("No VAT number for company")
      return
    }

    try {
      const response = await get(this.detailUrlValue, {
        query: { vat_number: company.vatNumber },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        if (data.status && data.response && data.response.results) {
          this.fillForm(data.response.results)
        }
      }
    } catch (error) {
      console.error("Detail fetch error:", error)
    }

    this.hideResults()
  }

  fillForm(companyData) {
    // Fill basic information
    if (this.hasNameInputTarget && companyData.fullName) {
      this.nameInputTarget.value = companyData.fullName
    }

    if (this.hasTaxNumberInputTarget && companyData.vatNumber) {
      this.taxNumberInputTarget.value = this.formatTaxNumber(companyData.vatNumber)
    }

    // Fill contact information
    const form = this.element.closest('form')
    if (form) {
      if (companyData.emailAddress) {
        const emailField = form.querySelector('input[name="company[email]"]')
        if (emailField) emailField.value = companyData.emailAddress
      }

      if (companyData.phoneNumber) {
        const phoneField = form.querySelector('input[name="company[phone_number]"]')
        if (phoneField) phoneField.value = companyData.phoneNumber
      }

      if (companyData.website) {
        const websiteField = form.querySelector('input[name="company[website]"]')
        if (websiteField) websiteField.value = companyData.website
      }

      // Fill banking information
      if (companyData.bankAccounts && companyData.bankAccounts.length > 0) {
        const bankAccount = companyData.bankAccounts[0]
        if (bankAccount.bankName) {
          const bankNameField = form.querySelector('input[name="company[primary_bank_name]"]')
          if (bankNameField) bankNameField.value = bankAccount.bankName
        }
        if (bankAccount.accountNumber) {
          const bankAccountField = form.querySelector('input[name="company[primary_bank_account]"]')
          if (bankAccountField) bankAccountField.value = bankAccount.accountNumber
        }
      }

      // Fill address information
      if (companyData.fullAddress) {
        this.parseAndFillAddress(companyData, form)
      }
    }
  }

  parseAndFillAddress(companyData, form) {
    // Fill postal code
    if (companyData.addressPostalCode) {
      const postalCodeField = form.querySelector('input[name="company[headquarters_postal_code]"]')
      if (postalCodeField) {
        postalCodeField.value = companyData.addressPostalCode
        // Trigger postcode controller if present
        const postcodeEvent = new Event('keyup', { bubbles: true })
        postalCodeField.dispatchEvent(postcodeEvent)
      }
    }

    // Fill city
    if (companyData.addressCity) {
      const cityField = form.querySelector('input[name="company[headquarters_city]"]')
      if (cityField) cityField.value = companyData.addressCity
    }

    // Fill country
    const countryField = form.querySelector('input[name="company[headquarters_country]"]')
    if (countryField) countryField.value = "Magyarország"

    // Parse street address from addressStreet field
    // Format from API: "Derkovits Gyula utca 53." - name type number
    if (companyData.addressStreet) {
      // Pattern: "street name type number" or "street name number"
      // Examples: "Derkovits Gyula utca 53." or "Main Street 42"
      const streetPattern = /^(.+?)\s+(utca|út|tér|körút|sétány|sor|park|aluljáró|átjáró|forduló|lejáró|rakpart|sugárút|fasor|köz|liget)\s+(\d+.*)$/i
      const match = companyData.addressStreet.match(streetPattern)
      
      if (match) {
        // Pattern matched: street name, type, and number
        const streetName = match[1].trim()
        const streetType = match[2].trim()
        const houseNumber = match[3].trim().replace(/\.$/, '') // Remove trailing dot if present

        const streetTypeField = form.querySelector('input[name="company[headquarters_public_area_type]"]')
        if (streetTypeField) streetTypeField.value = streetType

        const streetNameField = form.querySelector('input[name="company[headquarters_public_area_name]"]')
        if (streetNameField) streetNameField.value = streetName

        const houseNumberField = form.querySelector('input[name="company[headquarters_house_number]"]')
        if (houseNumberField) houseNumberField.value = houseNumber
      } else {
        // Try simpler pattern: "street name number" (without type)
        const simplePattern = /^(.+?)\s+(\d+.*)$/
        const simpleMatch = companyData.addressStreet.match(simplePattern)
        
        if (simpleMatch) {
          const streetName = simpleMatch[1].trim()
          const houseNumber = simpleMatch[2].trim().replace(/\.$/, '')

          const streetNameField = form.querySelector('input[name="company[headquarters_public_area_name]"]')
          if (streetNameField) streetNameField.value = streetName

          const houseNumberField = form.querySelector('input[name="company[headquarters_house_number]"]')
          if (houseNumberField) houseNumberField.value = houseNumber
        } else {
          // Just use the whole street as the name
          const streetNameField = form.querySelector('input[name="company[headquarters_public_area_name]"]')
          if (streetNameField) streetNameField.value = companyData.addressStreet
        }
      }
    }
  }

  hideResults() {
    // Hide all result containers
    const containers = this.element.querySelectorAll('[data-company-cegadat-api-target="resultsContainer"]')
    containers.forEach(container => {
      container.classList.remove("show")
      container.classList.add("d-none")
      container.innerHTML = ""
      container.className = "d-none"
    })
  }

  showLoading() {
    if (!this.activeInput) return
    
    // Find or create loading indicator for the active input
    let indicator = this.activeInput.parentElement.querySelector('[data-company-cegadat-api-target="loadingIndicator"]')
    if (!indicator) {
      indicator = document.createElement("div")
      indicator.className = "position-absolute top-50 end-0 translate-middle-y d-none me-2"
      indicator.setAttribute("data-company-cegadat-api-target", "loadingIndicator")
      indicator.innerHTML = `
        <div class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">Keresés...</span>
        </div>
      `
      this.activeInput.parentElement.appendChild(indicator)
    }
    indicator.classList.remove("d-none")
  }

  hideLoading() {
    // Hide all loading indicators
    const indicators = this.element.querySelectorAll('[data-company-cegadat-api-target="loadingIndicator"]')
    indicators.forEach(indicator => {
      indicator.classList.add("d-none")
    })
  }

  formatTaxNumber(taxNumber) {
    if (!taxNumber) return ''
    
    // Remove all non-digit characters
    const digits = taxNumber.replace(/\D/g, '')
    
    // Format as 8-1-2 (Hungarian tax number format)
    if (digits.length >= 11) {
      return `${digits.substring(0, 8)}-${digits.substring(8, 9)}-${digits.substring(9, 11)}`
    } else if (digits.length >= 9) {
      return `${digits.substring(0, 8)}-${digits.substring(8, 9)}-${digits.substring(9)}`
    } else if (digits.length >= 8) {
      return `${digits.substring(0, 8)}-${digits.substring(8)}`
    }
    
    // If not enough digits, return as is
    return digits
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

